/**
 * Question cards, without the drawing: the payload `ask_user` produces, and
 * the rules for what an answer is and whether a card has one.
 *
 * The server makes the card (`backend/assistant/clarify.py`); the person
 * answers it; the answer is an ordinary user message whose `content` says it in
 * words and whose `answers` says it by question id. Nothing is written back
 * onto the card, so whether it was answered is read from the turn after it -
 * which is what lets a chat reopened from history draw exactly what happened.
 */

import type { AssistantAnswers, AssistantCard } from '../../api/types.ts';
import type { AssistantHistoryTurn } from './assistant-history.ts';

export const QUESTIONS_CARD = 'clarify';

export type AnswerValue = string | number | string[];

export interface QuestionOption {
  value: string;
  note?: string;
}

export interface Question {
  id: string;
  kind: 'choice' | 'signal' | 'number' | 'text';
  label: string;
  hint?: string;
  options?: QuestionOption[];
  multiple?: boolean;
  suggested?: AnswerValue | null;
  unit?: string;
  ship_id?: string;
  assumed?: boolean;
}

export interface QuestionsPayload {
  id: string;
  title: string;
  questions: Question[];
}

/** The payload of a question card, or null for any other card. */
export function questionsOf(card: AssistantCard | null | undefined): QuestionsPayload | null {
  if (!card || card.kind !== QUESTIONS_CARD) return null;
  const payload = card.payload as Partial<QuestionsPayload>;
  if (typeof payload.id !== 'string' || !Array.isArray(payload.questions)) return null;
  return {
    id: payload.id,
    title: typeof payload.title === 'string' ? payload.title : 'A few things first',
    questions: payload.questions.filter((q): q is Question => !!q && typeof q.id === 'string'),
  };
}

/** Every question starts on its suggestion, so a right guess is one click. */
export function initialValues(payload: QuestionsPayload): Record<string, AnswerValue> {
  const out: Record<string, AnswerValue> = {};
  for (const q of payload.questions) {
    if (q.suggested !== undefined && q.suggested !== null) out[q.id] = q.suggested;
    else out[q.id] = q.multiple ? [] : q.kind === 'number' ? '' : '';
  }
  return out;
}

export function isEmpty(value: AnswerValue | undefined): boolean {
  if (value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim() === '';
}

export function sameValue(a: AnswerValue | undefined, b: AnswerValue | null | undefined): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const x = Array.isArray(a) ? [...a].sort() : [];
    const y = Array.isArray(b) ? [...b].sort() : [];
    return x.length === y.length && x.every((v, i) => v === y[i]);
  }
  return String(a ?? '') === String(b ?? '');
}

/** One answer as a person would read it: `60 kW`, `A, B`. */
export function displayValue(q: Question, value: AnswerValue | undefined): string {
  if (value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (q.kind === 'number') return q.unit ? `${value} ${q.unit}` : String(value);
  return String(value);
}

/** Numbers go back as numbers; everything else as picked. */
export function normalise(q: Question, value: AnswerValue | undefined): AnswerValue {
  if (q.kind === 'number') {
    const n = typeof value === 'number' ? value : Number(String(value ?? '').trim());
    return Number.isFinite(n) ? n : String(value ?? '');
  }
  return value ?? '';
}

/** The message a card's answers become: words for reading, ids for the model. */
export function answerMessage(
  payload: QuestionsPayload,
  values: Record<string, AnswerValue>,
): { content: string; answers: AssistantAnswers } {
  const normalised: Record<string, AnswerValue> = {};
  const lines: string[] = [];
  for (const q of payload.questions) {
    const value = normalise(q, values[q.id]);
    normalised[q.id] = value;
    lines.push(`${q.label}: ${displayValue(q, value)}`);
  }
  return { content: lines.join('\n'), answers: { to: payload.id, values: normalised } };
}

export type QuestionsStatus = 'open' | 'answered' | 'chat' | 'waiting';

/**
 * Where a question card stands, from the turns around it.
 *
 * * `answered` - the next message answered it from the card.
 * * `chat`     - the next message was typed instead; the card is done with.
 * * `waiting`  - nothing after it yet, but the chat is busy (an answer is on
 *   its way), so it cannot be answered twice.
 * * `open`     - the last thing in the chat, ready to answer.
 */
export function questionsStatus(
  turns: ReadonlyArray<AssistantHistoryTurn>,
  index: number,
  busy: boolean,
): QuestionsStatus {
  const payload = questionsOf(turns[index]?.card);
  const next = turns.slice(index + 1).find((t) => t.role === 'user');
  if (next) return payload && next.answers?.to === payload.id ? 'answered' : 'chat';
  return busy ? 'waiting' : 'open';
}

/** The card a user turn's answers belong to, searching back from it. */
export function answeredCard(
  turns: ReadonlyArray<AssistantHistoryTurn>,
  index: number,
): QuestionsPayload | null {
  const to = turns[index]?.answers?.to;
  if (!to) return null;
  for (let i = index - 1; i >= 0; i -= 1) {
    const payload = questionsOf(turns[i]?.card);
    if (payload?.id === to) return payload;
  }
  return null;
}
